import { cn } from '@rocco/utils/cn';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import type { FilterConfigModalItemColor } from '@rocco/ui/filter-sort/types/filter-config-modal';
import {
    getColorItemName,
    getSafeSwatchBackground,
} from '@rocco/ui/filter-sort/utils/color-swatch';
import type { MultipleValuesFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/multiple-values-filter-state';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { FilterStatesKey } from '@rocco/ui/filter-sort/view-models/filter-states';

interface RefineColorGridProps {
    filterConfigItem: FilterConfigModalItemColor;
    filterState: MultipleValuesFilterState;
    onUpdateFilterState: (
        filterKey: FilterStatesKey,
        newState: FilterState,
        shouldAggregate?: boolean,
    ) => void;
}

/**
 * Labelled colour grid for the refine drill-in: swatch plus name, because
 * colours here are taxonomy names, not raw colours — Gold, Bronze and Brown
 * are near-identical at swatch size, and colour alone as the identifier
 * fails users with colour vision deficiency.
 */
export const RefineColorGrid = (props: RefineColorGridProps) => {
    const { filterConfigItem, filterState, onUpdateFilterState } = props;
    const {
        searchFilters: { handleToggleMultipleValues },
    } = useFiltersContext();

    return (
        <div className="grid grid-cols-2 gap-x-4">
            {filterState.items.map((item) => {
                const name = getColorItemName(item.value);

                return (
                    <button
                        key={String(item.value)}
                        type="button"
                        aria-pressed={item.checked}
                        disabled={item.disabled}
                        className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded text-left focus:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:text-gray-400"
                        data-color={item.value}
                        onClick={() => {
                            if (!handleToggleMultipleValues) return;
                            onUpdateFilterState(
                                filterConfigItem.filterKey,
                                handleToggleMultipleValues(
                                    filterState,
                                    item,
                                    !item.checked,
                                ),
                            );
                        }}
                    >
                        <span
                            className={cn(
                                'size-5 shrink-0 rounded-full border border-gray-900/20',
                                item.checked &&
                                    'ring-2 ring-gray-900 ring-offset-1',
                            )}
                            style={{
                                background: getSafeSwatchBackground(item.label),
                                backgroundOrigin: 'border-box',
                            }}
                        />
                        <span
                            className={cn(
                                'truncate text-sm',
                                item.checked && 'font-medium',
                            )}
                        >
                            {name}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};
